import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Chip
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const EXPERIENCE_LEVELS = [
    { value: 'Expert', label: 'Expert' },
    { value: 'Senior', label: 'Senior' },
    { value: 'Junior', label: 'Junior' },
    { value: 'Regular', label: 'Regular' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Associate', label: 'Associate' }
];

const RECRUITMENT_TIMELINES = [
    '1 to 3 days',
    '3 to 7 days',
    '1 to 2 weeks',
    '2 to 4 weeks',
    'More than 4 weeks'
];

const PAY_RATES = [
    'per hour',
    'per day',
    'per week',
    'per month',
    'per year'
];

function JobForm({ open, onClose, jobId, companyId, onSaved }) {
    const { register, handleSubmit, formState: { errors }, reset, control, watch } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingJob, setLoadingJob] = useState(false);

    // Load dropdown options
    const { data: categoriesData } = useQuery('categories', async () => {
        const service = new HttpService();
        const data = await service.get('/job/category/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    });

    const { data: jobTypesData } = useQuery('jobTypes', async () => {
        const service = new HttpService();
        const data = await service.get('/job/jobtype/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    });

    const { data: schedulesData } = useQuery('schedules', async () => {
        const service = new HttpService();
        const data = await service.get('/job/schedule/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    });

    const { data: benefitsData } = useQuery('benefits', async () => {
        const service = new HttpService();
        const data = await service.get('/job/benefit/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    });

    const categories = Array.isArray(categoriesData) ? categoriesData : (categoriesData?.results || []);
    const jobTypes = Array.isArray(jobTypesData) ? jobTypesData : (jobTypesData?.results || []);
    const schedules = Array.isArray(schedulesData) ? schedulesData : (schedulesData?.results || []);
    const benefits = Array.isArray(benefitsData) ? benefitsData : (benefitsData?.results || []);

    useEffect(() => {
        if (open) {
            if (jobId) {
                loadJob();
            } else {
                // Default end_date to 3 months from now
                const defaultEndDate = dayjs().add(3, 'month');
                reset({
                    title: '',
                    description: '',
                    salary: '',
                    pay_minimum: '',
                    pay_maximum: '',
                    pay_rate: '',
                    address: '',
                    location_description: '',
                    number_of_hire: '',
                    contact_email: '',
                    experience_level: '',
                    recruitment_timeline: '',
                    start_date: null,
                    end_date: defaultEndDate,
                    category: '',
                    jobtypes: [],
                    schedules: [],
                    benefits: []
                });
                setError('');
            }
        }
    }, [open, jobId, reset]);

    const loadJob = async () => {
        if (!jobId) return;
        
        setLoadingJob(true);
        setError('');
        try {
            const job = await JobHttpService.loadJob(jobId);
            reset({
                title: job.title || '',
                description: job.description || '',
                salary: job.salary || '',
                pay_minimum: job.pay_minimum || '',
                pay_maximum: job.pay_maximum || '',
                pay_rate: job.pay_rate || '',
                address: job.address || '',
                location_description: job.location_description || '',
                number_of_hire: job.number_of_hire || '',
                contact_email: job.contact_email || '',
                experience_level: job.experience_level || '',
                recruitment_timeline: job.recruitment_timeline || '',
                start_date: job.start_date ? dayjs(job.start_date) : null,
                end_date: job.end_date ? dayjs(job.end_date) : null,
                category: job.category?.id || '',
                jobtypes: job.jobtypes?.map(jt => jt.id) || [],
                schedules: job.schedules?.map(s => s.id) || [],
                benefits: job.benefits?.map(b => b.id) || []
            });
        } catch (err) {
            console.error('Error loading job:', err);
            setError(err?.message || 'Failed to load job');
        } finally {
            setLoadingJob(false);
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                title: data.title,
                description: data.description,
                salary: data.salary,
                pay_minimum: data.pay_minimum ? parseFloat(data.pay_minimum) : null,
                pay_maximum: data.pay_maximum ? parseFloat(data.pay_maximum) : null,
                pay_rate: data.pay_rate,
                address: data.address,
                location_description: data.location_description,
                number_of_hire: data.number_of_hire,
                contact_email: data.contact_email,
                experience_level: data.experience_level,
                recruitment_timeline: data.recruitment_timeline,
                start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : null,
                end_date: data.end_date ? dayjs(data.end_date).format('YYYY-MM-DD') : null,
                company: companyId,
                category: data.category || null,
                jobtypes: data.jobtypes || [],
                schedules: data.schedules || [],
                benefits: data.benefits || []
            };

            let response;
            if (jobId) {
                response = await JobHttpService.updateJob(jobId, submitData);
            } else {
                response = await JobHttpService.createJob(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                onClose();
            } else {
                setError(response?.detail || response?.error || 'Failed to save job');
            }
        } catch (err) {
            console.error('Error saving job:', err);
            setError(err?.message || 'Failed to save job. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    {jobId ? 'Edit Job' : 'Create New Job'}
                </DialogTitle>
                <DialogContent>
                    {loadingJob && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loadingJob && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Job Title"
                                        {...register("title", {
                                            required: "Job title is required"
                                        })}
                                        error={Boolean(errors.title)}
                                        helperText={errors.title?.message}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Description"
                                        {...register("description")}
                                        error={Boolean(errors.description)}
                                        helperText={errors.description?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            label="Category"
                                            {...register("category")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {categories.map((cat) => (
                                                <MenuItem key={cat.id} value={cat.id}>
                                                    {cat.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Experience Level</InputLabel>
                                        <Select
                                            label="Experience Level"
                                            {...register("experience_level")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {EXPERIENCE_LEVELS.map((level) => (
                                                <MenuItem key={level.value} value={level.value}>
                                                    {level.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Number of Positions"
                                        type="number"
                                        {...register("number_of_hire")}
                                        error={Boolean(errors.number_of_hire)}
                                        helperText={errors.number_of_hire?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Recruitment Timeline</InputLabel>
                                        <Select
                                            label="Recruitment Timeline"
                                            {...register("recruitment_timeline")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {RECRUITMENT_TIMELINES.map((timeline) => (
                                                <MenuItem key={timeline} value={timeline}>
                                                    {timeline}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Pay Minimum"
                                        type="number"
                                        {...register("pay_minimum")}
                                        error={Boolean(errors.pay_minimum)}
                                        helperText={errors.pay_minimum?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Pay Maximum"
                                        type="number"
                                        {...register("pay_maximum")}
                                        error={Boolean(errors.pay_maximum)}
                                        helperText={errors.pay_maximum?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Contact Email"
                                        type="email"
                                        {...register("contact_email")}
                                        error={Boolean(errors.contact_email)}
                                        helperText={errors.contact_email?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Salary (Alternative)"
                                        {...register("salary")}
                                        error={Boolean(errors.salary)}
                                        helperText={errors.salary?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        {...register("address")}
                                        error={Boolean(errors.address)}
                                        helperText={errors.address?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Location Description"
                                        {...register("location_description")}
                                        error={Boolean(errors.location_description)}
                                        helperText={errors.location_description?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="start_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Start Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="end_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="End Date (Job Expiry)"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    minDate={dayjs()}
                                                    slotProps={{ textField: { fullWidth: true, helperText: 'Job will be hidden after this date' } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Pay Rate</InputLabel>
                                        <Select
                                            label="Pay Rate"
                                            {...register("pay_rate")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {PAY_RATES.map((rate) => (
                                                <MenuItem key={rate} value={rate}>
                                                    {rate}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Job Types</InputLabel>
                                        <Controller
                                            name="jobtypes"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    multiple
                                                    label="Job Types"
                                                    value={field.value || []}
                                                    onChange={field.onChange}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const jobType = jobTypes.find(jt => jt.id === value);
                                                                return <Chip key={value} label={jobType?.title || value} size="small" />;
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {jobTypes.map((jobType) => (
                                                        <MenuItem key={jobType.id} value={jobType.id}>
                                                            <Checkbox checked={(field.value || []).indexOf(jobType.id) > -1} />
                                                            {jobType.title}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Schedules</InputLabel>
                                        <Controller
                                            name="schedules"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    multiple
                                                    label="Schedules"
                                                    value={field.value || []}
                                                    onChange={field.onChange}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const schedule = schedules.find(s => s.id === value);
                                                                return <Chip key={value} label={schedule?.title || value} size="small" />;
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {schedules.map((schedule) => (
                                                        <MenuItem key={schedule.id} value={schedule.id}>
                                                            <Checkbox checked={(field.value || []).indexOf(schedule.id) > -1} />
                                                            {schedule.title}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Benefits</InputLabel>
                                        <Controller
                                            name="benefits"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    multiple
                                                    label="Benefits"
                                                    value={field.value || []}
                                                    onChange={field.onChange}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const benefit = benefits.find(b => b.id === value);
                                                                return <Chip key={value} label={benefit?.title || value} size="small" />;
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {benefits.map((benefit) => (
                                                        <MenuItem key={benefit.id} value={benefit.id}>
                                                            <Checkbox checked={(field.value || []).indexOf(benefit.id) > -1} />
                                                            {benefit.title}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading || loadingJob}
                    >
                        {loading ? <CircularProgress size={20} /> : (jobId ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default JobForm;
